import React, { forwardRef, Ref, useState } from 'react';
import { DateValidationError, LocalizationProvider, PickerChangeHandlerContext } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SxProps, Theme } from '@mui/material';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';
import { format, isBefore, setYear } from 'date-fns';

export const MIN_DATE_HARD_LIMIT = new Date(1800, 1, 1);

export interface IDatePickerInput {
  name: string;
  label?: string;
  fullWidth?: boolean;
  helperText?: string;
  disabled?: boolean;
  readOnly?: boolean;
  sx?: SxProps<Theme>;
  errormessage?: string;
  error?: boolean;
  minDate?: Date;
  maxDate?: Date;
  maxDaysInFuture?: number;
  maxDaysInPast?: number;
  noPreviousDates?: boolean;
  noFutureDates?: boolean;
  value?: any;
  onChange?: (value: string | null, context: PickerChangeHandlerContext<DateValidationError>) => void;
  shouldDisableDate?: (day: Date) => boolean;
  disableHighlightToday?: boolean;
  required?: boolean;
  timezone?: 'default' | 'UTC' | 'system' | string;
  dateOnly?: boolean;
  clearable?: boolean;
}

const VigoDatePicker = (
  {
    name,
    label,
    fullWidth,
    helperText,
    disabled,
    readOnly,
    sx,
    errormessage,
    error,
    minDate,
    maxDate,
    maxDaysInFuture,
    maxDaysInPast,
    noPreviousDates,
    noFutureDates,
    value,
    onChange,
    shouldDisableDate,
    disableHighlightToday = false,
    required = false,
    timezone = 'system',
    dateOnly,
    clearable = false
  }: IDatePickerInput,
  ref: Ref<HTMLInputElement>
) => {
  // State

  const [open, setOpen] = useState<boolean>(false);
  const today = new Date();

  // Hooks
  const { t } = useVigoTranslation();

  const handleChange = (value: string | null, context: PickerChangeHandlerContext<DateValidationError>): void => {
    if (isBefore(new Date(value ?? ''), MIN_DATE_HARD_LIMIT)) {
      let temp = new Date(value ?? '');
      temp = setYear(temp, 1800);
      value = temp.toISOString();
    }

    onChange?.(value, context);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        inputRef={ref}
        format="dd/MM/yyyy"
        label={t(label)}
        disabled={disabled}
        readOnly={readOnly}
        disableHighlightToday={disableHighlightToday}
        minDate={
          noPreviousDates
            ? today
            : maxDaysInPast !== undefined
            ? new Date(today.setTime(today.getTime() - maxDaysInPast * 86400000))
            : minDate
        }
        maxDate={
          noFutureDates
            ? today
            : maxDaysInFuture !== undefined
            ? new Date(today.setTime(today.getTime() + maxDaysInFuture * 86400000))
            : maxDate
        }
        timezone={timezone}
        value={value !== null ? new Date(value) : null}
        onChange={(value, context) => {
          // Convert value to a Date object if it's not null or undefined, so can avoid calling toISOString on null
          const date = value ? new Date(value) : null;

          if (dateOnly && date) {
            // Format the date and call handleChange if dateOnly is true
            handleChange(format(date, 'yyyy-MM-dd') + 'T00:00:00.000Z', context);

            return;
          }

          // Check if date is valid before calling toISOString
          if (date && !isNaN(date.getTime())) {
            const localDate = format(date, 'yyyy-MM-dd') + 'T00:00:00.000Z';
            handleChange(localDate, context);
          } else {
            // Handle invalid or null date
            handleChange(null, context);
          }
        }}
        open={open}
        onOpen={() => {
          if (!disabled && !readOnly) {
            setOpen(true);
          }
        }}
        onClose={() => setOpen(false)}
        shouldDisableDate={shouldDisableDate}
        slotProps={{
          field: { clearable: clearable },
          popper: {
            placement: 'bottom-start'
          },
          textField: {
            'aria-readonly': true,
            error: error,
            size: 'small',
            name: name,
            value: value || '',
            fullWidth: fullWidth,
            helperText: error ? t(errormessage) : t(helperText),
            sx: sx,
            required: required,
            onClick: () => {
              if (!disabled && !readOnly) {
                setOpen(true);
              }
            },
            onKeyDown: e => {
              if (e.key === 'Enter') {
                if (!disabled && !readOnly) {
                  setOpen(true);
                }
              }
            }
          }
        }}
      />
    </LocalizationProvider>
  );
};
export default forwardRef(VigoDatePicker);
